import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { Box, Flex, Heading, Text } from "@chakra-ui/react";

import { isCompleteAnnualMembership, isEssentialAnnualMembership } from "components/Membership/membershipHelper";
import StripeCheckoutWidget from "components/StripeCheckoutWidget";
import config from "config/config";
import { useLayout } from "contexts/LayoutContext/LayoutContext";
import useEffectOnce from "hooks/useEffectOnce";
import { Offer } from "models/Offers";
import { usePreferencesStore } from "stores/preferences";
import { formatValueToCurrency, getLocalPriceValue } from "stores/preferences/currencies";
import { PRODUCTS_NAME } from "types/products";

type IParams = {
  token: string;
};

const CheckoutFormView = ({ productName, offers }: { productName: PRODUCTS_NAME; offers: Record<string, Offer> }) => {
  const { setActiveStep } = useLayout();
  const { t } = useTranslation();
  const { token } = useParams<IParams>();
  const [searchParams] = useSearchParams();
  const currency = usePreferencesStore((state) => state.currency);

  const product = offers[productName];

  const essentialAnnual = isEssentialAnnualMembership(productName);

  const planTypeCopy = essentialAnnual ? t("pricing.plans.essential") : t("pricing.plans.complete");

  const baseCallbackUrl = `${config.dashboardUrl}/trips/${token}/upgrade/processing-payment`;

  const callbackUrl = new URL(`${baseCallbackUrl}?product-name=${product}`, window.location.origin);

  callbackUrl.search = searchParams.toString();
  const isCompleteMembership = isCompleteAnnualMembership(productName);

  const localPrice = getLocalPriceValue(product?.prices, currency);

  useEffectOnce(() => {
    setActiveStep(2);
  });

  return (
    <Box maxWidth={{ base: "100%", lg: "700px" }}>
      <Heading variant="h2" mb={5} data-testid="card-details">
        {t("payment.checkout.card_details")}
      </Heading>
      <Text fontSize="lg" fontWeight="normal">
        {t(`payment.checkout.${essentialAnnual ? "getting_description" : "upgrade_description"}`, {
          value: formatValueToCurrency(localPrice, currency),
          plan: planTypeCopy,
        })}
      </Text>
      <Text fontSize="lg" fontWeight="normal">
        {t("payment.checkout.plan_will_be_active")}
      </Text>
      <Flex mt={14}>
        <StripeCheckoutWidget
          key={currency}
          productName={productName}
          callbackUrl={callbackUrl.toString()}
          requireTerms
          recurring={essentialAnnual}
          tripUuid={token}
          isCompleteMembership={isCompleteMembership}
        />
      </Flex>
    </Box>
  );
};

export default CheckoutFormView;
